import api from "./api";

/**
 * Cache di sessione degli URL firmati S3 per chiave (`data-img-key`).
 *
 * Gli editor relazione ricostruiscono l'`innerHTML` a ogni cambio tab: senza
 * cache ogni `<img>` verrebbe ri-firmata e ricaricata. Qui ogni chiave si firma
 * una volta per TTL e l'URL identico permette al browser di usare la sua cache.
 * Le firme in corso sono deduplicate.
 */
const urlCache = new Map<string, { url: string; ts: number }>();
const inFlight = new Map<string, Promise<string | null>>();
// I presigned lato BE durano ~1 giorno: rinnoviamo con ampio margine.
const TTL_MS = 45 * 60 * 1000;

/** URL in cache (non scaduto) per la chiave, o null. Sincrono. */
export function getCachedImageUrl(key: string): string | null {
    const hit = urlCache.get(key);
    return hit && Date.now() - hit.ts < TTL_MS ? hit.url : null;
}

/** Firma (o riusa dalla cache) l'URL per la chiave. Deduplica le chiamate in volo. */
export async function getSignedImageUrl(key: string): Promise<string | null> {
    const cached = getCachedImageUrl(key);
    if (cached) return cached;
    const pending = inFlight.get(key);
    if (pending) return pending;
    const p = (async () => {
        try {
            const res = await api.get(`/filess/url-by-key`, { params: { key } });
            const url: string | undefined = res.data?.data;
            if (url) { urlCache.set(key, { url, ts: Date.now() }); return url; }
        } catch { /* non critico: link ancora valido o errore transitorio */ }
        return urlCache.get(key)?.url ?? null; // fallback all'ultimo noto
    })();
    inFlight.set(key, p);
    try { return await p; } finally { inFlight.delete(key); }
}

/**
 * Applica in modo sincrono gli URL in cache alle `<img data-img-key>` del nodo.
 * Va chiamata subito dopo aver impostato `innerHTML`, così le immagini non
 * partono con l'URL salvato, probabilmente scaduto.
 */
export function applyCachedImageUrls(root: HTMLElement | null): void {
    if (!root) return;
    root.querySelectorAll<HTMLImageElement>('img[data-img-key], img[data-img-preview-key]').forEach(img => {
        const key = previewKeyOf(img);
        if (!key) return;
        const url = getCachedImageUrl(key);
        if (url && img.getAttribute('src') !== url) img.setAttribute('src', url);
    });
}

/**
 * Chiave per l'anteprima nell'editor: la versione compressa
 * (`data-img-preview-key`) se presente, altrimenti `data-img-key`.
 * L'originale resta in `data-img-key` per il PDF ad alta risoluzione.
 */
function previewKeyOf(img: Element): string | null {
    return img.getAttribute('data-img-preview-key') || img.getAttribute('data-img-key');
}

/**
 * Garantisce a ogni `<img data-img-key>` un URL valido, firmando solo le chiavi
 * non in cache. Da chiamare al load e dopo ogni cambio di sezione.
 */
export async function refreshImageUrls(root: HTMLElement | null): Promise<void> {
    if (!root) return;
    const imgs = Array.from(root.querySelectorAll<HTMLImageElement>('img[data-img-key], img[data-img-preview-key]'));
    const keys = Array.from(new Set(
        imgs.map(previewKeyOf).filter((k): k is string => !!k)
    ));
    await Promise.all(keys.map(k => getSignedImageUrl(k)));
    imgs.forEach(img => {
        const key = previewKeyOf(img);
        if (!key) return;
        const url = getCachedImageUrl(key);
        if (url && img.getAttribute('src') !== url) img.setAttribute('src', url);
    });
}
